import { classMap } from 'lit/directives/class-map.js';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { LitElement, PropertyValues, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Question } from '../types';
import { TPCRouter } from '../utils/router';
import { trackClickEvent } from '../../events/clicks-tracker';

@customElement('tm-tpc-corrector-navbar')
export class TmTPCCorrectorNavbar extends LitElement {
    createRenderRoot() {
        return this;
    }

    @property({ type: Boolean })
    isLoading = false;

    @property({ type: Boolean })
    isLastPage = false;

    @property({ type: Number })
    usage = 0;

    @property({ type: Boolean })
    isPremiumUser = false;

    @property({ type: Boolean })
    error = false;

    @property({ type: Array })
    questions: Question[] = [];

    @property({ type: String })
    selectedQuestionUuid: string | number | undefined;

    @state()
    mustShowBottomFade = false;

    goToHistoryItem(item: string) {
        TPCRouter.getInstance().gotToQuestionViewPage(item);
    }

    checkBottomFade(element: HTMLElement) {
        if (element) {
            if (this.questions.length > 5 && !this.isPremiumUser) {
                this.mustShowBottomFade = true;
                return;
            }

            if (element.scrollHeight <= element.clientHeight) {
                this.mustShowBottomFade = false;
                return;
            }

            const scrollBottom = element.scrollTop + element.clientHeight;
            const threshold = element.scrollHeight - 100;
            if (scrollBottom < threshold) {
                this.mustShowBottomFade = true;
            } else if (scrollBottom >= threshold && this.isLastPage) {
                this.mustShowBottomFade = false;
            } else {
                this.mustShowBottomFade = true;
            }
        }
    }

    setupScrollListener() {
        const menu = document.getElementById('tpc-corrector-navbar');
        if (menu) {
            menu.addEventListener('scroll', () => {
                const scrollBottom = menu.scrollTop + menu.clientHeight;
                const threshold = menu.scrollHeight - 100;
                this.checkBottomFade(menu);
                if (
                    (scrollBottom >= threshold || menu.scrollHeight <= menu.clientHeight) &&
                    !this.isLastPage
                ) {
                    this.dispatchEvent(new CustomEvent('bottomScrollReached'));
                }
            });
        }
    }

    firstUpdated() {
        this.setupScrollListener();
        this.checkBottomFade(document.getElementById('tpc-corrector-navbar') as HTMLElement);
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.size !== 1 || !changedProperties.has('mustShowBottomFade')) {
            this.checkBottomFade(document.getElementById('tpc-corrector-navbar') as HTMLElement);
        }
    }

    renderFreeLimitReached() {
        return html`
            <div class="tpc-corrector-navbar-limit-card">
                <div class="tpc-corrector-navbar-limit-card-container">
                    <div class="tpc-corrector-navbar-limit-card-bg"></div>
                    <div class="tpc-corrector-navbar-limit-card-content">
                        <div class="tpc-corrector-navbar-limit-card--title">
                            Tira-dúvidas 24h com histórico completo!
                        </div>
                        <div>Pergunte sem limites e com registro por menos de R$1 por dia</div>
                        <a
                            href="/tmplus/"
                            class="ds-button ds-button--tmplus--invert"
                            @click="${() => {
                                trackClickEvent('cta-tmplus-assistant-history');
                            }}"
                            >Assine o Toda Matéria+</a
                        >
                    </div>
                </div>
            </div>
        `;
    }

    render() {
        return html`
            <div class="tpc-corrector-navbar" id="tpc-corrector-navbar">
                <div class="tpc-corrector-navbar-top-actions">
                    <button
                        class="ds-button ds-button--reverse tpc-corrector-navbar-new-exercise"
                        @click=${() => {
                            if (TPCRouter.getInstance().isNewExercisePage()) {
                                window.dispatchEvent(new CustomEvent('triedToGoToNewExercise'));
                                return;
                            }
                            window.scrollTo({
                                top: 0,
                                behavior: 'smooth',
                            });
                            TPCRouter.getInstance().goToNewExercisePage();
                        }}
                    >
                        <span class="icon icon-050-doc-write"></span>
                        Nova questão
                    </button>
                    <hr />
                </div>
                <ul class="tpc-corrector-navbar-history">
                    <li>
                        <div class="tpc-corrector-navbar-history-title">
                            <span class="icon icon-068-historic"></span>
                            Histórico de questões
                        </div>
                    </li>
                    ${this.questions.map(
                        (question) => html`
                            <li>
                                <button
                                    class="${classMap({
                                        'tpc-corrector-navbar-history-item': true,
                                        'tpc-corrector-navbar-history-item--selected':
                                            this.selectedQuestionUuid === question.uuid,
                                        'tpc-corrector-navbar-history-item--obfuscated':
                                            question.isObfuscated || false,
                                    })}"
                                    @click=${() => {
                                        if (
                                            this.selectedQuestionUuid === question.uuid ||
                                            question.isObfuscated
                                        ) {
                                            return;
                                        }
                                        window.scrollTo({
                                            top: 0,
                                            behavior: 'smooth',
                                        });
                                        this.goToHistoryItem(question.uuid);
                                    }}
                                    ?disabled=${question.isObfuscated}
                                >
                                    <div class="tpc-corrector-navbar-history-item-question">
                                        ${question.title}
                                    </div>
                                    <span class="icon icon-052-chevron"></span>
                                </button>
                            </li>
                        `
                    )}
                </ul>
                ${this.isLoading || this.error
                    ? html`<div class="tpc-corrector-navbar-loading">
                          <span class="tpc-corrector-navbar-loading--bar skeleton"></span>
                          <span class="tpc-corrector-navbar-loading--bar skeleton"></span>
                          <span class="tpc-corrector-navbar-loading--bar skeleton"></span>
                          <div class="tpc-corrector-navbar-loading--text">
                              ${this.isLoading
                                  ? html`<span class="icon icon-021-restart"></span> A carregar
                                        questões`
                                  : html`<span class="icon icon-054-alert-outline"></span> Ocorreu
                                        um erro,
                                        <button
                                            @click=${() => {
                                                this.dispatchEvent(
                                                    new CustomEvent('bottomScrollReached')
                                                );
                                            }}
                                        >
                                            tentar novamente
                                        </button>`}
                          </div>
                      </div>`
                    : nothing}
                ${this.mustShowBottomFade &&
                !this.isLoading &&
                !this.error 
                    ? html`<div class="tpc-corrector-navbar-bottom-fade"></div>`
                    : nothing}
                ${!this.isPremiumUser ? this.renderFreeLimitReached() : nothing}
            </div>
        `;
    }
}
